import { Link, Navigate, Outlet, useMatch } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { TeamSelector } from '../components/work/TeamSelector';
import { useGoalActivity, useGoals, useTeams } from '../hooks/useWorkManagement';
import { errorWithNothingCachedToShow } from '../lib/query-error';
import { activeTeamIdAtom } from '../stores/work/atoms';
import { WorkStarterPrompts } from '../components/work/WorkStarterPrompts';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

interface Crumb {
  label: string;
  to: string;
}

function WorkBreadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="work-breadcrumb">
      {crumbs.map((crumb, index) => (
        <span key={crumb.to} className="flex items-center gap-1">
          {index > 0 && <span aria-hidden="true">/</span>}
          {index === crumbs.length - 1 ? (
            <span className="font-medium text-foreground" data-testid="work-breadcrumb-current">
              {crumb.label}
            </span>
          ) : (
            <Link to={crumb.to} className="hover:text-foreground hover:underline">
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}

/**
 * Layout route; every level below resolves from the URL alone
 * (app-shell ADR-010).
 *
 * The view SHOWS state and is changed only by asking the docked agent
 * (app-shell ADR-013): it carries no create-team dialog, no
 * drag-to-transition and no chat panel of its own. The route's `handle` supplies the bottom dock
 * (app-shell ADR-016); the agent acts through `platform_call`
 * (app-shell ADR-019).
 */
export function WorkView() {
  const teamsQuery = useTeams();
  const teams = teamsQuery.data;

  const teamMatch = useMatch({ path: '/work/teams/:teamId', end: false });
  const goalMatch = useMatch({ path: '/work/teams/:teamId/goals/:goalId', end: false });
  const projectMatch = useMatch({
    path: '/work/teams/:teamId/goals/:goalId/projects/:projectId',
    end: false,
  });
  const teamTicketMatch = useMatch({ path: '/work/teams/:teamId/tickets/:ticketId', end: false });
  const projectTicketMatch = useMatch({
    path: '/work/teams/:teamId/goals/:goalId/projects/:projectId/tickets/:ticketId',
    end: false,
  });

  const teamId = teamMatch?.params.teamId ?? null;
  useCatalogSubscription('work', teamId ?? undefined);
  const goalId = goalMatch?.params.goalId ?? null;
  const projectId = projectMatch?.params.projectId ?? null;
  const ticketId = projectTicketMatch?.params.ticketId ?? teamTicketMatch?.params.ticketId ?? null;

  const { data: goals } = useGoals(teamId);
  const { data: activity } = useGoalActivity(goalId);

  // A failed REFETCH keeps prior data in v5, so only a read with nothing cached
  // may replace the outlet. Live board writes arrive via the work catalog holder.
  const teamsError = errorWithNothingCachedToShow(teams, teamsQuery.error);
  const noTeams = teamsQuery.isSuccess && teamsQuery.data.length === 0;

  const crumbs: Crumb[] = [{ label: 'Work', to: '/work' }];
  if (teamId) {
    const team = teams?.find((t) => t.id === teamId);
    crumbs.push({ label: team?.name ?? 'Team', to: `/work/teams/${teamId}` });
  }
  if (teamId && goalId) {
    const goal = goals?.find((g) => g.id === goalId);
    crumbs.push({
      label: goal?.objective ?? 'Goal',
      to: `/work/teams/${teamId}/goals/${goalId}/projects`,
    });
  }
  if (teamId && goalId && projectId) {
    const project = activity?.initiatives
      .flatMap((initiative) => initiative.projects)
      .find((p) => p.id === projectId);
    crumbs.push({
      label: project?.name ?? 'Project',
      to: `/work/teams/${teamId}/goals/${goalId}/projects/${projectId}`,
    });
  }
  if (ticketId) {
    const task = activity?.initiatives
      .flatMap((initiative) => initiative.projects)
      .flatMap((project) => project.tasks)
      .find((t) => t.id === ticketId);
    const base = projectTicketMatch?.pathname ?? teamTicketMatch?.pathname ?? '/work';
    crumbs.push({ label: task?.identifier ?? 'Ticket', to: base });
  }

  return (
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b bg-background px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold">Work</h1>
              <TeamSelector teamId={teamId} />
            </div>
          </div>
          <div className="mt-2">
            <WorkBreadcrumb crumbs={crumbs} />
          </div>
        </div>

        {teamsError ? (
          <div className="flex flex-1 items-center justify-center" data-testid="work-teams-error">
            <div className="text-destructive">Failed to load teams: {teamsError.message}</div>
          </div>
        ) : noTeams ? (
          <div className="flex flex-1 items-center justify-center" data-testid="work-empty-state">
            <div className="max-w-md space-y-4 text-center">
              <p className="text-muted-foreground">
                No teams yet. A team owns the board your tickets live on. Ask the agent below to
                make one — this view shows work, it does not create it.
              </p>
              <WorkStarterPrompts
                prompts={[
                  'Create a team called Platform with the prefix PLA.',
                  'What teams do we have, and what is on their boards?',
                ]}
              />
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <Outlet />
          </div>
        )}
      </div>
    </div>
  );
}

/** `/work` index — the only place the persisted team is read (app-shell ADR-010). */
export function WorkLanding() {
  const landingTeamId = useAtomValue(activeTeamIdAtom);
  const { data: teams, isLoading } = useTeams();

  if (isLoading) {
    return (
      <LoadingState label="Loading teams..." className="flex-1" />
    );
  }

  const restored = teams?.find((team) => team.id === landingTeamId);
  if (restored) {
    return <Navigate to={`/work/teams/${restored.id}`} replace />;
  }
  return (
    <div className="flex flex-1 items-center justify-center">
      <p className="text-muted-foreground">Select a team to view the board</p>
    </div>
  );
}
